import { useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from "react-router-dom";
import { NotificationManager } from 'react-notifications';
import { useMsal } from '@azure/msal-react';
import useFetchSensors from './useFetchSensors';
import useFetchInventorySummary from './useFetchInventorySummary';
import { msalConfig } from '../auth/authConfig';
import { SensorFormData, SensorDTO } from '../definitions/sensor';
import { errorHandler } from '../utils/helpers';
import useUserStore from '../store/userStore';
import { acquireToken } from '../utils/auth';

const prepareData = (data: SensorDTO): SensorDTO => ({
  ...data,
  cost: Number(data.cost) || 0,
});

interface EditSensorRequest {
  id?: string | number,
  isQuickEdit: boolean,
  token: string | null;
  setError: (data: any) => any;
  setLoading: (data: any) => any;
  navigate: (string) => void,
  fetchSensors: () => void,
  fetchInventorySummary: () => void,
  data: SensorDTO;
};

const editSensorRequest = async ({
    id,
    isQuickEdit,
    fetchSensors,
    fetchInventorySummary,
    token,
    setError,
    setLoading,
    navigate,
    data,
  }: EditSensorRequest): Promise<boolean> => {
  if (!id) return false;
  setLoading(true);
  let saved = false;
  try {
    const response = await axios.put(`${window.__env.REACT_APP_API_URL || process.env.REACT_APP_API_URL}/Sensor/${id}`, prepareData(data), {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json, text/plain, */*',
      },
    });
    if (response.status === 200) {
      saved = true;
      NotificationManager.success('Sensor Edited');
      navigate('/');
    }
  } catch (error) {
    errorHandler(error, setError);
  } finally {
    setLoading(false);
    if (isQuickEdit) {
      fetchInventorySummary();
      fetchSensors({ page: 1 });
    };
  }
  return saved;
};

interface EditSensor {
  // Resolves to true only when the API confirmed the update.
  editSensor: (formData: SensorFormData | any, quickId?: string | number) => Promise<boolean>;
  loading: boolean;
  error: Error | null;
};

const useEditSensor = (): EditSensor => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const { fetchSensors } = useFetchSensors();
  const { fetchInventorySummary } = useFetchInventorySummary();

  const { user } = useUserStore();

  const editSensor = async (formData, quickId?: string | number): Promise<boolean> => {
    if (accounts.length > 0) {
      const accessTokenRequest = {
        scopes: [`${msalConfig.auth.clientId}/.default`],
        account: accounts[0],
      };

      if (!user) {
        navigate('/sign-in');
        return false;
      }
      if (!quickId && !id) {
        navigate('/');
        return false;
      }

      const data: SensorDTO = {
        ...formData,
        userId: user.id,
      };

      try {
        const accessToken = await acquireToken(instance, accessTokenRequest);
        return await editSensorRequest({
          id: quickId || id,
          isQuickEdit: !!quickId,
          token: accessToken,
          fetchSensors,
          fetchInventorySummary,
          setError,
          setLoading,
          navigate,
          data,
        });
      } catch (error) {
        errorHandler(error, setError);
        return false;
      }
    } else {
      navigate('/sign-in');
      return false;
    }
  }

  return { editSensor, loading, error };
};

export default useEditSensor;
